import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MessageCircle, MapPin, Calendar, Clock, Mail, ShieldCheck, Star, CheckCircle, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { ReviewReferralBlock } from "@/components/ReviewReferralBlock";
import { AnimatedSection } from "@/components/AnimatedSection";
import { PrimaryCTA } from "@/components/PrimaryCTA";

export const Route = createFileRoute("/google-maps-garage-e14")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/google-maps-garage-e14",
      title: "Woodseer Autos on Google Maps | Garage in E14",
      description:
        "Find Woodseer Autos on Google Maps. DVSA-authorised MOT station in Poplar, E14 for MOTs, servicing, diagnostics, repairs and bodywork.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Google Maps · Garage in E14", path: "/google-maps-garage-e14" },
      ],
      jsonLd: [localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance))],
    }),
  component: GoogleMapsVerifyPage,
});

function GoogleMapsVerifyPage() {
  const biz = getBusinessConfig();
  const fire = (action: string) =>
    trackEvent("google_maps_verify_cta_click", { action, page: "/google-maps-garage-e14" });

  return (
    <>
      <section className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Google Maps verification · Garage in E14
          </p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold text-foreground tracking-tight leading-tight">
            You've found the right Woodseer Autos.
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            If you reached this page from our Google Maps listing, here's everything you need to
            verify the business details, get to the workshop, and book in.
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            <a
              href={biz.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fire("open_google_maps")}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              <MapPin className="h-4 w-4" /> Open in Google Maps
            </a>
          </div>
          <PrimaryCTA location="google-maps-e14-hero" intent="visit or booking" variant="light" />
        </div>
      </section>

      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 md:px-6 grid gap-8 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Official details</p>
              <h2 className="mt-2 text-xl font-bold text-foreground">Business name &amp; contact</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <Row label="Business name" value="Woodseer Autos" />
                <Row label="Phone" value={biz.phone} href={biz.phoneHref} onTrack={() => fire("call_details")} />
                <Row label="Public email" value="info@woodseerautos.co.uk" href="mailto:info@woodseerautos.co.uk" onTrack={() => fire("email")} />
                <Row label="Instagram" value="@woodseerautos" href={biz.instagramUrl} onTrack={() => fire("instagram")} />
                <Row label="MOT station" value="DVSA-authorised · V106376" />
                <Row label="MOT classes" value="1, 2, 3, 4, 5 &amp; 7" />
              </dl>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Two pins, two addresses</p>
              <h2 className="mt-2 text-xl font-bold text-foreground">Where to actually drive</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Our registered address and the yard entrance are different — your sat-nav should aim
                for Copenhagen Place to reach the workshop.
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <span className="font-bold text-foreground">Registered address:</span><br />
                    16 Pixley Street, Poplar, London E14 7DF
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <span className="font-bold text-foreground">Yard entrance / sat-nav:</span><br />
                    14 Copenhagen Place, London E14 7DF
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <span className="font-bold text-foreground">Hours:</span><br />
                    Mon–Sat 9:00–18:00 · Closed Sunday
                  </span>
                </li>
              </ul>
              <a
                href={biz.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fire("directions_yard")}
                className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
              >
                Directions to yard entrance <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-12 md:py-16 bg-secondary/40 border-y border-border">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight">
              Before you visit
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
              A short checklist to make the visit smoother — especially helpful if it's your first
              time at the Copenhagen Place yard.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                "Bring your vehicle registration document",
                "Bring your locking wheel-nut key if your car has one",
                "Call us first for urgent MOTs so we can confirm a slot",
                "Tell us about any warning lights and when they appeared",
                "Let us know if you drive for work or cover high mileage",
                "Note your MOT, licensing or service deadline if you have one",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/85">
                  <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-2xl border border-border bg-card p-5">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <h3 className="mt-3 text-base font-bold text-foreground">DVSA-authorised</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Station V106376 · classes 1, 2, 3, 4, 5 and 7.
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <Star className="h-6 w-6 text-primary" />
                <h3 className="mt-3 text-base font-bold text-foreground">Locally reviewed</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  4.4 rating from 116+ Google reviews from drivers across E14 and East London.
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <Mail className="h-6 w-6 text-primary" />
                <h3 className="mt-3 text-base font-bold text-foreground">Same-day reply</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Call, WhatsApp or request a booking and we'll come back within workshop hours.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              <Link to="/official-business-information" className="text-primary font-bold hover:underline inline-flex items-center gap-1">
                Full business information <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <span className="text-border" aria-hidden="true">·</span>
              <Link to="/reviews" className="text-primary font-bold hover:underline inline-flex items-center gap-1">
                Read all reviews <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <span className="text-border" aria-hidden="true">·</span>
              <Link to="/contact" className="text-primary font-bold hover:underline inline-flex items-center gap-1">
                Contact &amp; directions <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-12 md:py-16 bg-secondary/30 border-t border-border">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <ReviewReferralBlock location="google_maps_verify" />
          </div>
        </section>
      </AnimatedSection>
    </>
  );
}

function Row({
  label,
  value,
  href,
  onTrack,
}: {
  label: string;
  value: string;
  href?: string;
  onTrack?: () => void;
}) {
  const inner = (
    <span
      className={href ? "text-primary font-bold hover:underline" : "text-foreground font-semibold"}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 last:border-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd>
        {href ? (
          <a href={href} onClick={onTrack} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
            {inner}
          </a>
        ) : (
          inner
        )}
      </dd>
    </div>
  );
}
